/**
 * Regression Test Dashboard
 * 
 * Admin page showing nightly regression test results, history,
 * and the ability to trigger manual test runs.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Timer,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import apiClient from '../../api/client';

const API_PREFIX = '/api/v1/admin/regression';

const fetchHistory = async () => {
  const res = await apiClient.get(`${API_PREFIX}/history?limit=20`);
  return res.data;
};

const triggerRun = async () => {
  const res = await apiClient.post(`${API_PREFIX}/run`);
  return res.data;
};

const StatusBadge = ({ status }) => {
  const config = {
    passed: { label: 'Passed', variant: 'default', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
    failed: { label: 'Failed', variant: 'destructive', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
    running: { label: 'Running', variant: 'secondary', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    error: { label: 'Error', variant: 'destructive', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
  };
  const c = config[status] || config.error;
  return <Badge data-testid={`status-badge-${status}`} className={c.className}>{c.label}</Badge>;
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const TestDetailRow = ({ test }) => (
  <div className="flex items-center justify-between py-1.5 px-2 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50">
    <div className="flex items-center gap-2">
      {test.passed ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
      )}
      <span className="text-gray-700 dark:text-gray-300">{test.name}</span>
    </div>
    <span className="text-xs text-gray-500 dark:text-gray-400 max-w-[300px] truncate">{test.detail}</span>
  </div>
);

const RunDetailCard = ({ run }) => {
  const [expanded, setExpanded] = useState(false);
  const errorLoadTests = run.error_load_results?.tests || [];
  const securityFindings = run.security_scan_results?.findings || [];

  return (
    <div
      data-testid={`run-card-${run.id}`}
      className="border rounded-lg p-4 bg-white dark:bg-gray-900 dark:border-gray-700 transition-all"
    >
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        data-testid={`run-toggle-${run.id}`}
      >
        <div className="flex items-center gap-3">
          <StatusBadge status={run.status} />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {formatDate(run.started_at)}
          </span>
          <Badge variant="outline" className="text-xs capitalize">{run.trigger}</Badge>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="text-gray-600 dark:text-gray-400">{run.passed_tests}</span>
            {run.failed_tests > 0 && (
              <>
                <XCircle className="w-4 h-4 text-red-500 ml-2" />
                <span className="text-gray-600 dark:text-gray-400">{run.failed_tests}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Timer className="w-3.5 h-3.5" />
            {formatDuration(run.duration_seconds)}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4" data-testid={`run-details-${run.id}`}>
          {errorLoadTests.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Error & Load Tests
              </h4>
              <div className="space-y-0.5">
                {errorLoadTests.map((t, i) => <TestDetailRow key={i} test={t} />)}
              </div>
            </div>
          )}
          {securityFindings.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Security Scan
              </h4>
              <div className="space-y-0.5">
                {securityFindings.map((f, i) => <TestDetailRow key={i} test={f} />)}
              </div>
            </div>
          )}
          {run.summary && (
            <p className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t dark:border-gray-700">
              {run.summary}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const RegressionDashboard = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['regression-history'],
    queryFn: fetchHistory,
    refetchInterval: 15000,
  });

  const mutation = useMutation({
    mutationFn: triggerRun,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regression-history'] });
    },
  });

  const runs = data?.data || [];
  const latest = runs[0];
  const passCount = runs.filter(r => r.status === 'passed').length;
  const failCount = runs.filter(r => r.status === 'failed').length;
  const totalRuns = runs.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-testid="regression-dashboard" className="space-y-6 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            Regression Tests
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Nightly automated tests run at 2:00 AM UTC
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="refresh-btn"
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          </Button>
          <Button
            data-testid="run-now-btn"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || runs.some(r => r.status === 'running')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {mutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Run Now
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Latest Status</p>
                <div className="mt-1.5">
                  {latest ? <StatusBadge status={latest.status} /> : <span className="text-sm text-gray-400">No runs</span>}
                </div>
              </div>
              <Shield className={`w-8 h-8 ${latest?.status === 'passed' ? 'text-emerald-500' : latest?.status === 'failed' ? 'text-red-500' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Pass Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                  {totalRuns > 0 ? `${Math.round((passCount / totalRuns) * 100)}%` : '-'}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Total Runs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">{totalRuns}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Failures</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">{failCount}</p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${failCount > 0 ? 'text-red-500' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pass/Fail Trend Bar */}
      {totalRuns > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Recent Run History</CardTitle>
            <CardDescription className="text-xs">Last {totalRuns} runs (newest first)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-1 h-8" data-testid="trend-bar">
              {runs.slice(0, 20).map((run, i) => (
                <div
                  key={run.id}
                  title={`${formatDate(run.started_at)} - ${run.status}`}
                  className={`flex-1 rounded-sm transition-all cursor-pointer hover:opacity-80 ${
                    run.status === 'passed' ? 'bg-emerald-500' :
                    run.status === 'failed' ? 'bg-red-500' :
                    run.status === 'running' ? 'bg-blue-400 animate-pulse' :
                    'bg-orange-400'
                  }`}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Run History List */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Run History</h2>
        {runs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No regression tests have been run yet.</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Click "Run Now" to trigger the first test, or wait for the nightly schedule.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="run-history-list">
            {runs.map(run => <RunDetailCard key={run.id} run={run} />)}
          </div>
        )}
      </div>

      {/* Mutation feedback */}
      {mutation.isError && (
        <div className="fixed bottom-4 right-4 bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg shadow-lg text-sm">
          {mutation.error?.response?.data?.detail || 'Failed to trigger test run'}
        </div>
      )}
      {mutation.isSuccess && (
        <div className="fixed bottom-4 right-4 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 px-4 py-2 rounded-lg shadow-lg text-sm">
          Test run started! Results will appear shortly.
        </div>
      )}
    </div>
  );
};

export default RegressionDashboard;
